// EmiCalculator — interactive widget shown on the marketplace car detail
// page. Sliders for down-payment, tenure, and interest rate; large hero
// showing "₹X,XXX / month". Fires analytics events so we can measure
// conversion intent BEFORE signing a lender partnership (Rupyy / CARS24).
//
// Zero external deps — pure client-side computation via lib/emi.js.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calculator, TrendingDown, ArrowRight, IndianRupee } from 'lucide-react';
import { calcEmi, fmtInr, EMI_DEFAULTS } from '../../lib/emi';
import { trackFinanceEvent, ensureSessionId } from '../../lib/financeAnalytics';

const Row = ({ label, value, sub }) => (
  <div className="flex items-baseline justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="text-slate-900 font-medium">
      {value}
      {sub && <span className="text-[11px] text-slate-400 ml-1">{sub}</span>}
    </span>
  </div>
);

const Slider = ({ label, value, min, max, step, onChange, suffix, testid }) => (
  <label className="block">
    <div className="flex items-baseline justify-between mb-1">
      <span className="text-xs text-slate-500 uppercase tracking-wider">{label}</span>
      <span className="text-sm font-semibold text-slate-900" data-testid={`${testid}-value`}>
        {value}{suffix}
      </span>
    </div>
    <input
      type="range"
      min={min} max={max} step={step} value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full h-1 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
      data-testid={testid}
    />
  </label>
);

export default function EmiCalculator({ carId, askingPrice, onApply }) {
  const [dpPct, setDpPct] = useState(EMI_DEFAULTS.downPaymentPct);
  const [rate, setRate]   = useState(EMI_DEFAULTS.interestRate);
  const [tenure, setTenure] = useState(EMI_DEFAULTS.tenureMonths);
  const viewedRef = useRef(false);

  useEffect(() => {
    if (viewedRef.current) return;
    if (askingPrice > 0) {
      viewedRef.current = true;
      ensureSessionId();
      trackFinanceEvent({ car_id: carId, event_type: 'emi_view' });
    }
  }, [carId, askingPrice]);

  const dpAmount = useMemo(() => Math.round((askingPrice || 0) * (dpPct / 100)), [askingPrice, dpPct]);
  const principal = Math.max(0, (askingPrice || 0) - dpAmount);
  const emi = useMemo(
    () => calcEmi({ principal, annualRatePct: rate, tenureMonths: tenure }),
    [principal, rate, tenure],
  );
  const totalInterest = Math.max(0, emi * tenure - principal);
  const totalPayable  = emi * tenure + dpAmount;

  // Debounce analytics for slider adjustments so we don't fire on every
  // keystroke — one event per ~500ms of idle time.
  useEffect(() => {
    if (!viewedRef.current) return;
    const t = setTimeout(() => {
      trackFinanceEvent({
        car_id: carId, event_type: 'emi_adjust',
        payload: { dp_pct: dpPct, rate, tenure_months: tenure, emi },
      });
    }, 500);
    return () => clearTimeout(t);
  }, [carId, dpPct, rate, tenure, emi]);

  const handleApply = () => {
    trackFinanceEvent({
      car_id: carId, event_type: 'emi_apply_clicked',
      payload: { dp_pct: dpPct, rate, tenure_months: tenure, emi },
    });
    onApply?.({ dpAmount, emi, tenure, rate });
  };

  if (!askingPrice) return null;

  return (
    <section
      className="mt-6 bg-white border border-teal-200 rounded-2xl p-5 shadow-[0_1px_2px_rgba(15,118,110,0.08)]"
      data-testid="emi-calculator"
    >
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-full bg-teal-50 flex items-center justify-center">
          <Calculator size={15} className="text-teal-700" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-900 leading-tight">EMI calculator</h3>
          <p className="text-[11px] text-slate-500">Estimate your monthly payment</p>
        </div>
      </div>

      {/* Hero — monthly EMI */}
      <div className="bg-gradient-to-br from-teal-50 to-teal-100/50 border border-teal-200 rounded-xl p-4 mb-4">
        <p className="text-[10px] text-teal-700 uppercase tracking-widest font-semibold">Est. monthly EMI</p>
        <p className="text-3xl font-bold text-teal-900 mt-0.5 tabular-nums" data-testid="emi-hero-value">
          ₹ {fmtInr(emi)}<span className="text-sm font-normal text-teal-600">/mo</span>
        </p>
        <p className="text-[11px] text-teal-700 mt-1 inline-flex items-center gap-1">
          <TrendingDown size={11} />
          On ₹ {fmtInr(principal)} loan · {tenure} months
        </p>
      </div>

      {/* Sliders */}
      <div className="space-y-4 mb-4">
        <Slider
          label="Down payment" value={dpPct} suffix="%"
          min={EMI_DEFAULTS.minDownPaymentPct} max={EMI_DEFAULTS.maxDownPaymentPct} step={5}
          onChange={setDpPct} testid="emi-slider-dp"
        />
        <Slider
          label="Tenure" value={tenure} suffix=" months"
          min={EMI_DEFAULTS.minTenure} max={EMI_DEFAULTS.maxTenure} step={6}
          onChange={setTenure} testid="emi-slider-tenure"
        />
        <Slider
          label="Interest rate" value={rate} suffix="%"
          min={EMI_DEFAULTS.minRate} max={EMI_DEFAULTS.maxRate} step={0.5}
          onChange={setRate} testid="emi-slider-rate"
        />
      </div>

      {/* Breakdown */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5 mb-4">
        <Row label="Down payment" value={`₹ ${fmtInr(dpAmount)}`} />
        <Row label="Loan amount"  value={`₹ ${fmtInr(principal)}`} />
        <Row label="Total interest" value={`₹ ${fmtInr(totalInterest)}`} sub={`over ${tenure} mo`} />
        <div className="pt-1.5 mt-1.5 border-t border-slate-200 flex items-baseline justify-between">
          <span className="text-sm text-slate-700 font-medium">Total payable</span>
          <span className="text-sm font-bold text-slate-900" data-testid="emi-total-payable">
            ₹ {fmtInr(totalPayable)}
          </span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleApply}
        className="w-full inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 rounded-lg text-sm transition"
        data-testid="emi-apply-btn"
      >
        <IndianRupee size={15} /> Get financing options
        <ArrowRight size={14} />
      </button>
      <p className="text-[10px] text-slate-400 text-center mt-2 leading-tight">
        Indicative EMI. Actual rate depends on lender + credit profile.
        Rates from 10–20% p.a. typical for used cars.
      </p>
    </section>
  );
}
